import { useTranslation } from 'react-i18next';
import { Clock, ClockAlert, Hand } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ACTIVE_TICKET_STATUSES, effectiveResolveDeadline } from '@nbh/shared';
import type { TicketStatus } from '@nbh/shared';
import { formatSlaSpan } from '../../lib/format';

/**
 * The SLA breach badges — a matched pair of vocabularies over the job's two promises.
 *
 * "Claim overdue" and "Fix overdue" name the two stages in the words staff already
 * use, and deliberately collide with nothing: the gold "Unclaimed" pill in
 * `QueueParts` is an offer (D31), these are faults, and a row legibly carries both
 * at once — which is exactly the true state of a job nobody has picked up past its
 * window, and why the two badges must be different words.
 *
 * Three visual weights, so the kinds read as different things at a glance:
 * status pills fill saturated, live breaches are TINTED (translucent fill), settled
 * breaches are OUTLINED — the same "outline means historical" convention StatusBadge
 * already uses for CLOSED and CANCELLED.
 *
 * All state is computed client-side from raw instants the summary carries, never
 * shipped as an enum: queue rows sit cached in Dexie, and a cached state goes stale
 * while a computation from a stored deadline keeps ticking (Rule 4).
 */

/** How close a live fix deadline gets before the quiet "due in" badge appears. */
const APPROACHING_MINUTES = 15;

export type BreachKind =
  | 'claim-overdue'
  | 'fix-overdue'
  | 'fix-due-soon'
  | 'claimed-late'
  | 'fixed-late';

export interface BreachBadgeState {
  kind: BreachKind;
  /** Pre-rendered by formatSlaSpan; settled kinds compute it from the record, so it never ticks. */
  span: string;
}

/** The slice of a ticket summary (or detail) the badge logic reads. */
export interface BreachTimestamps {
  status: TicketStatus;
  slaAckDeadline: string;
  slaResolveDeadline: string;
  /** Undefined tolerated: a queue row cached in Dexie before these fields shipped. */
  acknowledgedAt?: string | null;
  resolvedAt?: string | null;
  currentHoldStartedAt?: string | null;
}

/**
 * Which badges this job carries right now. Claim first, always — when both promises
 * are broken, the one that explains the other leads.
 */
export function breachBadgesFor(ticket: BreachTimestamps, now: Date): BreachBadgeState[] {
  const live = ACTIVE_TICKET_STATUSES.includes(ticket.status);
  const badges: BreachBadgeState[] = [];

  const ackDeadline = new Date(ticket.slaAckDeadline);
  const acknowledgedAt = ticket.acknowledgedAt ? new Date(ticket.acknowledgedAt) : null;
  if (acknowledgedAt) {
    // Settled: computed from the record, not the clock, so the words stay past
    // tense and the number never moves again.
    if (acknowledgedAt.getTime() > ackDeadline.getTime()) {
      badges.push({ kind: 'claimed-late', span: formatSlaSpan(ackDeadline, acknowledgedAt, 'overdue') });
    }
  } else if (live && now.getTime() > ackDeadline.getTime()) {
    badges.push({ kind: 'claim-overdue', span: formatSlaSpan(ackDeadline, now, 'overdue') });
  }

  const rawResolve = new Date(ticket.slaResolveDeadline);
  const resolvedAt = ticket.resolvedAt ? new Date(ticket.resolvedAt) : null;
  if (resolvedAt) {
    if (resolvedAt.getTime() > rawResolve.getTime()) {
      badges.push({ kind: 'fixed-late', span: formatSlaSpan(rawResolve, resolvedAt, 'overdue') });
    }
  } else if (live) {
    // The one correction that keeps this honest (D6): an open hold credits the
    // deadline minute for minute, so a job waiting on a vendor is never "Fix
    // overdue" for the vendor's time — and lateness accrued before the hold stays
    // frozen rather than forgiven. Same rule the CSV export applies.
    const effective = effectiveResolveDeadline(
      {
        slaResolveDeadline: rawResolve,
        currentHoldStartedAt: ticket.currentHoldStartedAt
          ? new Date(ticket.currentHoldStartedAt)
          : null,
      },
      now,
    );
    if (now.getTime() > effective.getTime()) {
      badges.push({ kind: 'fix-overdue', span: formatSlaSpan(effective, now, 'overdue') });
    } else if (effective.getTime() - now.getTime() <= APPROACHING_MINUTES * 60_000) {
      badges.push({ kind: 'fix-due-soon', span: formatSlaSpan(effective, now, 'remaining') });
    }
  }

  return badges;
}

/**
 * The 3px left-edge marker for a row carrying a live breach, in the breach colour —
 * claim's red outranks fix's amber because claim renders first for the same reason.
 * Null for settled and approaching states: the edge marks a fault that is happening.
 */
export function breachEdgeClass(badges: BreachBadgeState[]): string | null {
  if (badges.some((badge) => badge.kind === 'claim-overdue')) return 'border-l-red-400';
  if (badges.some((badge) => badge.kind === 'fix-overdue')) return 'border-l-amber-400';
  return null;
}

interface BreachStyle {
  Icon: LucideIcon | null;
  frame: string;
  labelKey: string;
}

const STYLES: Record<BreachKind, BreachStyle> = {
  // Live: tinted — translucent fill, light text, one weight below the gold
  // "Unclaimed" pill so an offer and a fault on one row read as different things.
  'claim-overdue': {
    Icon: Hand,
    frame: 'bg-red-500/15 text-red-200',
    labelKey: 'queue.breach.claimOverdue',
  },
  'fix-overdue': {
    Icon: ClockAlert,
    frame: 'bg-amber-500/15 text-amber-200',
    labelKey: 'queue.breach.fixOverdue',
  },
  'fix-due-soon': {
    Icon: Clock,
    frame: 'border border-slate-500 text-slate-300',
    labelKey: 'queue.breach.fixDueSoon',
  },
  // Settled: outlined, no glyph — historical, like StatusBadge's CLOSED treatment.
  'claimed-late': {
    Icon: null,
    frame: 'border border-red-400/70 text-red-300',
    labelKey: 'queue.breach.claimedLate',
  },
  'fixed-late': {
    Icon: null,
    frame: 'border border-amber-400/70 text-amber-300',
    labelKey: 'queue.breach.fixedLate',
  },
};

export function BreachBadge({ state }: { state: BreachBadgeState }) {
  const { t } = useTranslation();
  const { Icon, frame, labelKey } = STYLES[state.kind];

  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${frame}`}
    >
      {Icon && <Icon className="size-3.5" aria-hidden="true" />}
      {t(labelKey, { span: state.span })}
    </span>
  );
}
